<script setup lang="ts">
  // Unified history results: skeletons / error / empty states + card grid with
  // server-driven "Cargar más" (mirrors -pos PosHistoryList.vue).
  // variant 'workspace' scrolls internally (full-bleed POS route); 'content'
  // renders a plain block and lets the document scroll (back-office route).
  import { computed, getCurrentInstance } from 'vue';
  import type { IOrderListItem } from '@/domain/pos/pos-types';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiScrollable from '@/components/pos-v2/ui/PosUiScrollable.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import PosV2OrderCard from '@/components/pos-v2/main/PosV2OrderCard.vue';

  const props = withDefaults(
    defineProps<{
      items: IOrderListItem[];
      total: number;
      isLoading: boolean;
      isLoadingMore: boolean;
      /**
       * Optional with a null default: Vue 2.7 can't validate a required prop
       * against a `null` union member (warns "null is not a constructor").
       */
      error?: string | null;
      hasMore: boolean;
      /** True when any filter narrows the list (drives the empty-state copy). */
      hasFilters: boolean;
      variant?: 'workspace' | 'content';
    }>(),
    { variant: 'workspace', error: null }
  );

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string): string {
    return String(proxy.$t(key));
  }

  const emit = defineEmits<{
    (e: 'order-click', item: IOrderListItem): void;
    (e: 'load-more'): void;
    (e: 'retry'): void;
    (e: 'clear-filters'): void;
  }>();

  const isWorkspace = computed(() => props.variant === 'workspace');

  // Wrapper: internal scroller on the POS route, plain block on the
  // back-office route (percentage heights don't resolve there anyway).
  const resultsWrapper = computed(() =>
    isWorkspace.value ? PosUiScrollable : 'div'
  );

  const resultsWrapperProps = computed(() =>
    isWorkspace.value
      ? {
          orientation: 'vertical',
          variant: 'overlay',
          theme: 'page',
          size: 'sm',
          scrollClass: 'history-scroll',
        }
      : {}
  );
</script>

<template>
  <section class="pos-v2-history-list" :class="`variant-${variant}`">
    <!-- Error -->
    <PosUiEmptyState
      v-if="error"
      icon="alert-circle-outline"
      :title="i18nT('posV2.historyLoadError')"
      :description="error"
      class="list-state"
    >
      <template #action>
        <PosUiButton
          color="primary"
          variant="solid"
          size="lg"
          icon="refresh"
          @click="emit('retry')"
        >
          {{ $t('posV2.retry') }}
        </PosUiButton>
      </template>
    </PosUiEmptyState>

    <!-- Initial loading -->
    <div v-else-if="isLoading && items.length === 0" class="loading-state">
      <div class="loading-grid">
        <PosUiSkeleton v-for="i in 9" :key="i" class="order-skeleton" />
      </div>
    </div>

    <!-- Empty -->
    <PosUiEmptyState
      v-else-if="items.length === 0"
      :icon="hasFilters ? 'magnify' : 'history'"
      :title="
        hasFilters ? i18nT('posV2.noResults') : i18nT('posV2.historyEmptyTitle')
      "
      :description="
        hasFilters ? undefined : i18nT('posV2.historyEmptyDescription')
      "
      class="list-state"
    >
      <template v-if="hasFilters" #action>
        <PosUiButton
          color="neutral"
          variant="outline"
          size="lg"
          icon="close"
          @click="emit('clear-filters')"
        >
          {{ $t('posV2.clearSearch') }}
        </PosUiButton>
      </template>
    </PosUiEmptyState>

    <!-- Results -->
    <component
      :is="resultsWrapper"
      v-else
      :class="isWorkspace ? 'history-scrollable' : 'history-content'"
      v-bind="resultsWrapperProps"
    >
      <div class="orders-grid">
        <PosV2OrderCard
          v-for="item in items"
          :key="item.id"
          :order="item"
          show-date
          @click="emit('order-click', item)"
        />
      </div>

      <div v-if="hasMore" class="load-more">
        <PosUiButton
          color="neutral"
          variant="soft"
          size="lg"
          icon="chevron-down"
          :loading="isLoadingMore"
          class="load-more-button"
          @click="emit('load-more')"
        >
          {{ $t('posV2.historyLoadMore') }}
        </PosUiButton>
        <span class="load-more-hint">
          {{
            $t('posV2.historyShowing', { shown: items.length, total: total })
          }}
        </span>
      </div>
    </component>
  </section>
</template>

<style scoped lang="scss">
  .pos-v2-history-list {
    @apply tw-flex tw-flex-col;

    &.variant-workspace {
      @apply tw-min-h-0 tw-flex-1 tw-overflow-hidden;
    }

    // Inset canvas well: white order cards need neutral behind them inside the
    // white content-page frame (white-on-white blends completely).
    &.variant-content {
      @apply tw-bg-canvas;
    }

    .list-state {
      @apply tw-flex-1;
    }

    .loading-state {
      @apply tw-flex-1 tw-p-4;

      .loading-grid {
        @apply tw-grid tw-gap-3;
        grid-template-columns: repeat(auto-fill, minmax(min(290px, 100%), 1fr));

        .order-skeleton {
          @apply tw-h-32 tw-rounded-xl;
        }
      }
    }

    .history-scrollable {
      @apply tw-relative tw-min-h-0 tw-flex-1;

      :deep(.history-scroll) {
        @apply tw-flex tw-h-full tw-flex-col tw-gap-4 tw-overflow-y-auto tw-p-4;
      }
    }

    .history-content {
      @apply tw-flex tw-flex-col tw-gap-4 tw-p-4;
    }

    .orders-grid {
      @apply tw-grid tw-gap-3;
      grid-template-columns: repeat(auto-fill, minmax(min(290px, 100%), 1fr));
    }

    .load-more {
      @apply tw-flex tw-flex-col tw-items-center tw-gap-2 tw-py-4;

      .load-more-button {
        @apply tw-min-w-48;
      }

      .load-more-hint {
        @apply tw-text-xs tw-tabular-nums tw-text-neutral-400;
      }
    }
  }
</style>
